import classes from "@/styles/fulfilment-partners/purolator.module.css";
import Image from "next/image";
import {
  Card,
  CardBody,
  Container,
  Button,
  CardTitle,
  CardText,
  CardImg,
  Row,
  Col,
} from "reactstrap";
import Link from "next/link";
import { CSSProperties, ReactNode, useState } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { purolatorImages } from "@/utils/images";
import { routes } from "@/utils/routes";
import { useRouter } from "next/router";

type ColWrapperProps = {
  children: ReactNode;
};

const PurolatorMain = () => {
  const isMobile = useIsMobile();
  const router = useRouter();

  const cardStyle: CSSProperties = {
    width: isMobile ? "12rem" : "18rem",
    height: isMobile ? "15rem" : "16rem",
    alignItems: "center",
    textAlign: "center",
    cursor: "pointer",
  };

  const choiceCardStyle: CSSProperties = {
    alignItems: "center",
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "#ecf6f9",
    height: "13rem",
    width: "12rem",
  };

  const imageStyle: CSSProperties = {
    width: "35%",
    height: isMobile ? "27%" : "32%",
  };

  const choiceImageStyle: CSSProperties = {
    width: "75%",
    height: isMobile ? "27%" : "36%",
  };

  const cardStylePS: CSSProperties = {
    width: isMobile ? "12rem" : "18rem",
  };

  const cardBody = {
    tag: (isMobile ? "h6" : "h5") as React.ElementType,
  };

  const purolatorChoiceTextColor: CSSProperties = {
    color: "#012fa7",
    fontWeight: "bold",
  };

  const ColWrapper: React.FC<ColWrapperProps> = ({ children }) => (
    <Col sm="6" md="4" lg="2" className="mb-4">
      {children}
    </Col>
  );

  const redirectSpecializedServicesPage = () => {
    router.push(routes.purolatorSpecializedServices);
  };

  return (
    <main className={classes.container}>
      <section>
        <Container>
          <Image
            src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_01}
            alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_01}
            height={1156}
            width={4128}
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </Container>
      </section>
      <section className="my-3 container">
        <p>
          <b style={{ fontSize: "25px" }}>About Purolator</b>
        </p>
        <p>
          Purolator is a leading integrated freight, package and logistics
          provider, delivering packages to, from and within Canada. With our
          Canadian reach and U.S. expertise, we’ve continuously raised the bar
          for supply chain solutions for over six decades.
        </p>
        <p>
          <b style={{ fontSize: "22px" }}>
            Discover all that we have to offer for your shipping needs.
          </b>
        </p>
        <p>
          <b style={{ color: "#009", fontSize: "20px" }}>
            Service designed with you in mind
          </b>
          <br />
          Whether it’s across town or across the country, you’ll get reliable,
          professional shipping that will satisfy —and impress—your customers.
          Purolator Services include:
        </p>
      </section>
      <section>
        <Container>
          <Row className="justify-content-evenly">
            <Col sm="6">
              <Card
                body
                style={{
                  backgroundColor: "#ecf6f9",
                  height: "90%",
                }}
                className="mb-4"
              >
                <CardTitle tag="h5" style={{ color: "#012fa7" }}>
                  Expedited Shipping
                </CardTitle>
                <CardText>
                  Multiple and flexible shipping to meet your needs.
                </CardText>
              </Card>
            </Col>
            <Col sm="6">
              <Card
                body
                style={{
                  backgroundColor: "#ecf6f9",
                  height: "90%",
                }}
                className="mb-4"
              >
                <CardTitle tag="h5" style={{ color: "#012fa7" }}>
                  Experience{" "}
                </CardTitle>
                <CardText>
                  Over 25 years’ experience of cross-border logistics, customs
                  guidance and expertise.
                </CardText>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
      <section className="my-3 container">
        <div style={{ paddingBottom: "20px" }}>
          <b style={{ fontSize: "22px" }}>Why ShipVista chooses Purolator</b>
        </div>
        <Container>
          <Row className="justify-content-evenly">
            {/* 1 */}
            <ColWrapper>
              <Card style={choiceCardStyle}>
                <CardBody>
                  <Image
                    alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_09}
                    src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_09}
                    width={700}
                    height={400}
                    style={choiceImageStyle}
                  />
                  <CardText>
                    The{" "}
                    <span style={purolatorChoiceTextColor}>
                      only courier in Canada
                    </span>{" "}
                    to offer Domestic Ground delivery by 9:00 am<sup>1</sup>
                  </CardText>
                </CardBody>
              </Card>
            </ColWrapper>
            {/* 2 */}
            <ColWrapper>
              <Card style={choiceCardStyle}>
                <CardBody>
                  <Image
                    alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_10}
                    src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_10}
                    style={choiceImageStyle}
                    width={700}
                    height={400}
                  />
                  <CardText>
                    <span style={purolatorChoiceTextColor}>
                      25+ years experience
                    </span>{" "}
                    crossing the Canadian/U.S.border
                    <sup>2</sup>
                  </CardText>
                </CardBody>
              </Card>
            </ColWrapper>
            {/* 3 */}
            <ColWrapper>
              <Card style={choiceCardStyle}>
                <CardBody>
                  <Image
                    alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_11}
                    src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_11}
                    style={choiceImageStyle}
                    width={700}
                    height={400}
                  />
                  <CardText>
                    Largest nationally{" "}
                    <span style={purolatorChoiceTextColor}>
                      integrated freight and courier
                    </span>{" "}
                    network in Canada<sup>3</sup>
                  </CardText>
                </CardBody>
              </Card>
            </ColWrapper>
            {/* 4 */}
            <ColWrapper>
              <Card style={choiceCardStyle}>
                <CardBody>
                  <Image
                    alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_12}
                    src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_12}
                    style={choiceImageStyle}
                    width={700}
                    height={400}
                  />
                  <CardText>
                    Delivers to{" "}
                    <span style={purolatorChoiceTextColor}>100%</span> of all
                    Canadian postal codes<sup>4</sup>
                  </CardText>
                </CardBody>
              </Card>
            </ColWrapper>
            {/* 5 */}
            <ColWrapper>
              <Card style={choiceCardStyle}>
                <CardBody>
                  <Image
                    alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_13}
                    src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_13}
                    style={choiceImageStyle}
                    width={700}
                    height={400}
                  />
                  <CardText>
                    One of the{" "}
                    <span style={purolatorChoiceTextColor}>
                      most trusted brands
                    </span>{" "}
                    in the delivery service category
                    <sup>5</sup>
                  </CardText>
                </CardBody>
              </Card>
            </ColWrapper>
          </Row>
        </Container>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: "space-evenly",
          }}
        >
          <Link href={routes.purolatorTackleHunger}>
            <Image
              src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_14}
              alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_14}
              height={isMobile ? 200 : 288}
              width={isMobile ? 385 : 455}
            />
          </Link>
          <Link href={routes.purolatorEnvironmentSustainability}>
            <Image
              src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_15}
              alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_15}
              height={isMobile ? 200 : 288}
              width={isMobile ? 385 : 455}
            />
          </Link>
        </div>
      </section>
      <section className="my-3 container">
        <p>
          <b style={{ fontSize: "22px" }}>Purolator Service Levels</b>
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div>
            <p>
              <b style={{ color: "#009" }}>Shipping in Canada</b>
              <br />
              No matter where your customers are shipping in Canada – from
              Kelowna to St. John’s or anywhere in between, Purolator delivers.
              Choose from a variety of delivery services, including guaranteed
              overnight services so you can grow your business to every corner
              of Canada.
            </p>
            <p style={{ color: "#009" }}>
              <Link href={`${routes.home}`}>Get Quote</Link>
            </p>
          </div>
          <div>
            <p>
              <b style={{ color: "#009" }}>
                How to Ship from Canada to the U.S.
              </b>
              <br />
              With more than 25 years of cross-border shipping experience,
              shipping to the U.S. is a simple and seamless process with
              Purolator. Learn how to ship from Canada to the U.S. or create a
              shipment now<sup>3</sup>.
            </p>
            <p style={{ color: "#009" }}>
              <Link href={`${routes.register}`}>Ship Now</Link>
            </p>
          </div>
        </div>
      </section>
      {/* divider */}
      {/* <section className="my-3 container">
        <div
          style={{
            width: "100%",
            height: "2px",
            backgroundColor: "blue",
            margin: "20px 0",
          }}
        />
      </section>
      <section className="my-3 container">
        <p>
          <b style={{ fontSize: "22px" }}>Special Shipping Services</b>
        </p>
        <div
          style={{
            display: "flex",
            alignItems: isMobile ? "start" : "center",
            gap: "50px",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <div style={{ flex: "1" }}>
            <p>Customize your shipping with add-on services.</p>
            <Button
              style={{
                width: "100%",
                fontWeight: "bold",
                backgroundColor: "#012fa7",
              }}
              onClick={redirectSpecializedServicesPage}
            >
              See Purolator Services on ShipVista
            </Button>
          </div>
          <div style={{ flex: "1" }}>
            <p style={{ display: "flex" }}>
              <span style={{ marginRight: "8px" }}>
                <FaCircleCheck style={{ fontSize: "24px", color: "green" }} />
              </span>
              Request Special Handling services for shipments that need extra
              care
            </p>
            <p style={{ display: "flex" }}>
              <span style={{ marginRight: "8px" }}>
                <FaCircleCheck style={{ fontSize: "24px", color: "green" }} />
              </span>
              Ship regulated or controlled goods confidently with our Dangerous
              Goods service
            </p>
            <p style={{ display: "flex" }}>
              <span style={{ marginRight: "8px" }}>
                <FaCircleCheck style={{ fontSize: "24px", color: "green" }} />
              </span>
              Protect your shipments with durable and sturdy packaging options
              free of charge
            </p>
          </div>
        </div>
      </section> */}
      {/* divider */}
      {/* <section className="my-3 container">
        <div
          style={{
            width: "100%",
            height: "2px",
            backgroundColor: "blue",
            margin: "20px 0",
          }}
        />
      </section> */}
      <section className="my-3 container">
        <p>
          <b style={{ fontSize: "22px" }}>Packaging Solutions</b>
        </p>
        <p>
          Protect your shipment with durable and sturdy packaging available free
          of charge at Purolator Shipping Centres.
        </p>
      </section>
      <section className="my-3 container">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-evenly",
            gap: isMobile ? "5px" : "10px",
            fontSize: isMobile ? "0.9rem" : "1rem",
          }}
        >
          <Card style={cardStylePS}>
            <CardImg
              alt="Card image cap"
              src={`${purolatorImages.Ship_Vista_Page_Refresh_EN_4_30}`}
              height="40%"
            />
            <CardBody>
              <CardTitle style={{ color: "#012fa7" }} tag={cardBody.tag}>
                Purolator Express® Box
              </CardTitle>
              <CardText>
                A durable corrugated cardboard shipping box that is ideal for
                binders, large documents and other items that may require
                handling protection.
              </CardText>
            </CardBody>
          </Card>
          <Card style={cardStylePS}>
            <CardImg
              alt="Card image cap"
              src={`${purolatorImages.Ship_Vista_Page_Refresh_EN_4_31}`}
              height="40%"
            />
            <CardBody>
              <CardTitle style={{ color: "#012fa7" }} tag={cardBody.tag}>
                Purolator Express® Pack
              </CardTitle>
              <CardText>
                A convenient, easy-to-use, sealable bag that is tear- and
                water-resistant. For shipments up to 3 lbs., use the Purolator
                Express Pack to get your package to its destination via the
                fastest mode.
              </CardText>
            </CardBody>
          </Card>
          <Card style={cardStylePS}>
            <CardImg
              alt="Card image cap"
              src={`${purolatorImages.Ship_Vista_Page_Refresh_EN_4_32}`}
              height="40%"
            />
            <CardBody>
              <CardTitle style={{ color: "#012fa7" }} tag={cardBody.tag}>
                Purolator Express® Envelope
              </CardTitle>
              <CardText>
                A legal-sized cardboard shipping envelope that is ideal for your
                important documents.
              </CardText>
            </CardBody>
          </Card>
          <Card style={cardStylePS}>
            <CardImg
              alt="Card image cap"
              src={`${purolatorImages.Ship_Vista_Page_Refresh_EN_4_33}`}
              height="40%"
            />
            <CardBody>
              <CardTitle style={{ color: "#012fa7" }} tag={cardBody.tag}>
                Customer Packaging
              </CardTitle>
              <CardText>
                Prefer to use your own packaging? No problem. Be sure to review
                our packaging guidelines and tips, or come by one of our
                Purolator Shipping Centre locations for additional assistance
                and advice.
              </CardText>
            </CardBody>
          </Card>
        </div>
      </section>
      {/* divider */}
      <section className="my-3 container">
        <div
          style={{
            width: "100%",
            height: "2px",
            backgroundColor: "blue",
            margin: "20px 0",
          }}
        />
      </section>
      <section className="my-3 container">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: isMobile ? "5px" : "10px",
            justifyContent: "space-evenly",
            fontSize: isMobile ? "0.9rem" : "1rem",
          }}
        >
          <Link href={`${routes.trackShipment}`}>
            <Card className="my-2" color="primary" outline style={cardStyle}>
              <CardBody>
                <Image
                  alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_53}
                  src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_53}
                  style={imageStyle}
                  height={200}
                  width={200}
                />

                <p>
                  <span className={classes.blue_bold}>Track a Shipment</span>
                  <br />
                  Avoid missed deliveries. Track your package with SMS
                  notifications. <br />
                  <span className={classes.blue_bold}>Track now</span>
                </p>
              </CardBody>
            </Card>
          </Link>
          <Link href={`${routes.home}`}>
            <Card className="my-2" color="primary" outline style={cardStyle}>
              <CardBody>
                <Image
                  alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_54}
                  src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_54}
                  style={imageStyle}
                  width={200}
                  height={200}
                />

                <p>
                  <span className={classes.blue_bold}>Shipping in Canada</span>{" "}
                  <br /> Choose from a range of delivery services designed for
                  your unique shipping needs.
                  <br />
                  <span className={classes.blue_bold}>Ship now</span>
                </p>
              </CardBody>
            </Card>
          </Link>
          <Link href={`${routes.purolatorShipNow}`}>
            <Card className="my-2" color="primary" outline style={cardStyle}>
              <CardBody>
                <Image
                  alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_55}
                  src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_55}
                  style={imageStyle}
                  width={200}
                  height={200}
                />

                <p>
                  <span className={classes.blue_bold}>
                    How to Pack, Seal, and Label a Shipment
                  </span>{" "}
                  <br /> Proper packaging helps ensure we can deliver our
                  shipments on time and in great condition.
                  <br />
                  <span className={classes.blue_bold}>More info</span>
                </p>
              </CardBody>
            </Card>
          </Link>
          {/* <Link href={`${routes.help}`}>
            <Card className="my-2" color="primary" outline style={cardStyle}>
              <CardBody>
                <Image
                  alt={purolatorImages.Ship_Vista_Page_Refresh_EN_4_56}
                  src={purolatorImages.Ship_Vista_Page_Refresh_EN_4_56}
                  style={imageStyle}
                  width={200}
                  height={200}
                />

                <p>
                  <span className={classes.blue_bold}>
                    Service Alerts &amp; Updates
                  </span>{" "}
                  <br />
                  Stay informed of service disruptions with our up-to-date
                  service alerts
                  <br />
                  <span className={classes.blue_bold}>Read now</span>
                </p>
              </CardBody>
            </Card>
          </Link> */}
        </div>
      </section>
      {/* divider */}
      <section className="my-3 container">
        <div
          style={{
            width: "100%",
            height: "2px",
            backgroundColor: "blue",
            margin: "20px 0",
          }}
        />
      </section>
      <section className="my-3 container" style={{ fontSize: "12px" }}>
        Legal
        <br />
        1 2024 Courier Comparison Research Analysis Report by HelloInfo
        (completed Jan -Feb 2024). <br />
        2 Sourced from publicly available information on company websites (March
        2024). <br />
        3 Gustavson 2019 Brand Trust Index study conducted by University of
        Victoria School of Business among French and English speaking Canadian
        consumers aged 18 +. Survey data was collected from February 5 to 27,
        2019. Purolator ranks #1 in the delivery services category. <br />
        4 Purolator Inc., Service Directory, 2024. <br />5 Gustavson 2023 Brand
        Trust Index conducted by the Gustavson School of Business surveyed 9,200
        Canadian consumers aged 18+. Ranked and evaluated trust in over 400
        brands.
      </section>
    </main>
  );
};

export default PurolatorMain;
